import { useState, useEffect, useRef } from 'react';

interface UseInViewOptions {
  /** 提前觸發的邊距，讓圖片在進入畫面前就開始載入 */
  rootMargin?: string;
  /** 只觸發一次後不再觀察（懶載入用） */
  once?: boolean;
}

/**
 * Intersection Observer hook
 * 用途：偵測元素是否進入可視區域，常用於圖片懶載入
 */
export function useInView<T extends HTMLElement = HTMLDivElement>(options: UseInViewOptions = {}) {
  const { rootMargin = '400px 0px', once = true } = options;
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // 瀏覽器不支援時直接顯示
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, once]);

  return { ref, inView };
}
